'use client';

import useEditorRights from "@/hooks/useEditorRights";
import { Box, Button, Collapse, Fade, Stack, useMediaQuery } from "@mui/material";
import { Cancel, CheckCircle, Restore, Sync } from "@mui/icons-material";
import { usePathname } from "next/navigation";
import { alpha } from "@mui/material/styles";
import ApprovalDialog from "@/app/features/shared/__to_be_integrated/ui/dialogs/ApprovalDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import SubmitButton from "@/app/features/shared/editor-controls/SubmitButton";
import Typography from "@mui/material/Typography";
import { AutosaveState } from "@/app/lib/hooks/useAutosave";

export type FloatingEditorMenuAction = {
    label: string;
    onClick: () => void;
    startIcon?: React.ReactNode;
    disabled?: boolean;
};

const MENU_TRANSITION_MS = 220;
const ENTRY_BUTTON_HEIGHT_PX = 44;

export default function FloatingEditorMenu({
    autosaveState = undefined,
    isDraftModeEnabled,
    toggleDraftMode,
    hasChanges,
    onPublish,
    onDiscard,
    actions = []
}: {
    autosaveState?: AutosaveState;
    isDraftModeEnabled: boolean;
    toggleDraftMode: (enable: boolean, returnTo: string) => void | Promise<void>;
    hasChanges?: boolean;
    onDiscard?: () => void
    onPublish?: () => void | Promise<unknown>;
    actions?: FloatingEditorMenuAction[];
}) {
    const hasEditorRights = useEditorRights();
    const pathname = usePathname();
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const transitionDuration = prefersReducedMotion ? 0 : MENU_TRANSITION_MS;

    const [isDiscardApprovalDialogOpen, setIsDiscardApprovalDialogOpen] = useState<boolean>(false);
    const [isSwitchingToPublic, setIsSwitchingToPublic] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const isPublishingRef = useRef(false);
    const modeRedirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const autosaveIcon = useMemo(() => {
        switch (autosaveState) {
            case 'running':
                return (
                    <Sync color="primary" className="origin-center animate-spin"/>
                )
            case 'success':
                return <CheckCircle color="success" />
            case 'error':
                return <Cancel color="error" />
        }
    }, [autosaveState]);

    const autosaveText = useMemo(() => {
        switch (autosaveState) {
            case 'running':
                return "Autom. speichern ..."
            case 'success':
                return "Autom. gespeichert!"
            case 'error':
                return "Autom. speichern fehlgeschlagen!"
        }
    }, [autosaveState])

    useEffect(() => {
        return () => {
            if (modeRedirectTimeoutRef.current) {
                clearTimeout(modeRedirectTimeoutRef.current);
            }
        };
    }, []);

    const triggerDraftModeSwitch = (enable: boolean) => {
        if (modeRedirectTimeoutRef.current) {
            clearTimeout(modeRedirectTimeoutRef.current);
        }

        const executeSwitch = () => {
            void toggleDraftMode(enable, pathname);
        };

        if (prefersReducedMotion) {
            executeSwitch();
            return;
        }

        modeRedirectTimeoutRef.current = setTimeout(executeSwitch, transitionDuration);
    };

    const handleEnableDraftMode = () => {
        if (isDraftModeEnabled) return;
        triggerDraftModeSwitch(true);
    };

    const handleDisableDraftMode = () => {
        if (isSwitchingToPublic) return;
        setIsSwitchingToPublic(true);
        triggerDraftModeSwitch(false);
    };

    const handlePublish = () => {
        if (isPublishingRef.current || !onPublish) return;

        isPublishingRef.current = true;
        setIsPublishing(true);

        void (async () => {
            try {
                await onPublish();
            } finally {
                isPublishingRef.current = false;
                setIsPublishing(false);
            }
        })();
    };

    const showsEntryButton = !isDraftModeEnabled || isSwitchingToPublic;

    if (hasEditorRights) {
        return (
            <>
                <Box
                    sx={{
                        zIndex: 1100,
                        position: 'fixed',
                        right: 16,
                        bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
                        viewTransitionName: "floating-editor-entry",
                    }}
                >
                    <Fade in={!!autosaveState && !showsEntryButton} timeout={transitionDuration}>
                        <Box sx={{ justifyItems: 'center', zIndex: 100, overflow: 'hidden' }}>
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={(theme) => ({
                                    alignItems: 'center',
                                    paddingX: 2,
                                    paddingY: 0.75,
                                    border: `1px solid ${theme.palette.breakout.main}`,
                                    borderBottom: 0,
                                    borderTopLeftRadius: "10px",
                                    borderTopRightRadius: "10px",
                                    backgroundColor: alpha(theme.palette.common.black, 0.8),
                                    boxShadow: theme.shadows[4]
                                })}
                            >
                                {autosaveIcon}
                                <Typography variant="body1" sx={{ fontSize: 14, color: "breakout.light", fontWeight: 700 }}>
                                    {autosaveText}
                                </Typography>
                            </Stack>
                        </Box>
                    </Fade>
                    <Box
                        sx={{ zIndex: 200, display: "flex", alignItems: "stretch" }}
                    >
                        <Collapse
                            in={showsEntryButton}
                            orientation="horizontal"
                            timeout={transitionDuration}
                            easing="cubic-bezier(0.2, 0, 0, 1)"
                        >
                            <Box
                                sx={(theme) => ({
                                    border: `1px solid ${theme.palette.breakout.main}`,
                                    borderRadius: "2px 10px 2px 10px",
                                    backgroundColor: alpha(theme.palette.common.black, 0.75),
                                    boxShadow: theme.shadows[4],
                                    overflow: "hidden",
                                })}
                            >
                                <Button
                                    sx={{
                                        px: 2,
                                        py: 0,
                                        height: ENTRY_BUTTON_HEIGHT_PX,
                                        minHeight: ENTRY_BUTTON_HEIGHT_PX,
                                        textDecoration: "none",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        color: "breakout.main",
                                        borderRadius: "2px 10px 2px 10px",
                                        whiteSpace: "nowrap",
                                        '&:hover, &:focus-visible': {
                                            textDecoration: "none",
                                            color: "breakout.light",
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                    onClick={handleEnableDraftMode}
                                >
                                    Bearbeiten
                                </Button>
                            </Box>
                        </Collapse>
                        <Collapse
                            in={!showsEntryButton}
                            orientation="horizontal"
                            timeout={transitionDuration}
                            easing="cubic-bezier(0.2, 0, 0, 1)"
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                            >
                                {actions.length > 0 && (
                                    <Stack
                                        direction="row"
                                        spacing={0}
                                        sx={(theme) => ({
                                            border: `1px solid ${alpha(theme.palette.breakout.main, 0.35)}`,
                                            borderRadius: "2px 10px 2px 10px",
                                            backgroundColor: alpha(theme.palette.common.black, 0.75),
                                            boxShadow: theme.shadows[4],
                                            overflow: "hidden",
                                            '& > *:not(:first-of-type)': {
                                                borderLeft: `1px solid ${alpha(theme.palette.breakout.main, 0.35)}`,
                                            },
                                        })}
                                    >
                                        {actions.map((action) => (
                                            <Button
                                                key={action.label}
                                                sx={{
                                                    px: 2, py: 0, height: ENTRY_BUTTON_HEIGHT_PX,
                                                    minHeight: ENTRY_BUTTON_HEIGHT_PX,
                                                    fontWeight: 700,
                                                    textTransform: "none",
                                                    borderRadius: 0,
                                                    whiteSpace: "nowrap",
                                                    '&:hover, &:focus-visible': {
                                                        color: "breakout.light", backgroundColor: "transparent",
                                                    },
                                                }}
                                                startIcon={action.startIcon}
                                                disabled={action.disabled}
                                                onClick={action.onClick}
                                            >
                                                {action.label}
                                            </Button>
                                        ))}
                                    </Stack>
                                )}

                                <Stack
                                    direction="row"
                                    spacing={0}
                                    sx={(theme) => ({
                                        border: `1px solid ${alpha(theme.palette.breakout.main, 0.35)}`,
                                        borderRadius: "2px 10px 2px 10px",
                                        backgroundColor: alpha(theme.palette.common.black, 0.75),
                                        boxShadow: theme.shadows[4],
                                        overflow: "hidden",
                                        '& > *:not(:first-of-type)': {
                                            borderLeft: `1px solid ${alpha(theme.palette.breakout.main, 0.35)}`,
                                        },
                                    })}
                                >
                                    {hasChanges && (
                                        <>
                                            <SubmitButton
                                                semantic="publish"
                                                state={{
                                                    isSubmitting: isPublishing,
                                                    isValid: true,
                                                    success: null,
                                                }}
                                                sx={{
                                                    px: 2,
                                                    py: 0,
                                                    height: ENTRY_BUTTON_HEIGHT_PX,
                                                    minHeight: ENTRY_BUTTON_HEIGHT_PX,
                                                    color: "breakout.main",
                                                    fontWeight: 700,
                                                    textTransform: "none",
                                                    borderRadius: 0,
                                                    '&:hover, &:focus-visible': {
                                                        color: "breakout.light",
                                                        backgroundColor: "transparent",
                                                    },
                                                }}
                                                disabled={!hasChanges || isPublishing}
                                                onClick={handlePublish}
                                            />
                                            <Button
                                                sx={{
                                                    px: 2,
                                                    py: 0,
                                                    height: ENTRY_BUTTON_HEIGHT_PX,
                                                    minHeight: ENTRY_BUTTON_HEIGHT_PX,
                                                    color: "warning.light",
                                                    fontWeight: 700,
                                                    textTransform: "none",
                                                    borderRadius: 0,
                                                    '&:hover, &:focus-visible': {
                                                        color: "warning.main",
                                                        backgroundColor: "transparent",
                                                    },
                                                }}
                                                color="warning"
                                                startIcon={<Restore />}
                                                onClick={() => setIsDiscardApprovalDialogOpen(true)}
                                            >
                                                Verwerfen
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        sx={{
                                            px: 2,
                                            py: 0,
                                            height: ENTRY_BUTTON_HEIGHT_PX,
                                            minHeight: ENTRY_BUTTON_HEIGHT_PX,
                                            color: "error.light",
                                            fontWeight: 700,
                                            textTransform: "none",
                                            borderRadius: 0,
                                            '&:hover, &:focus-visible': {
                                                color: "error.main",
                                                backgroundColor: "transparent",
                                            },
                                        }}
                                        color="error"
                                        startIcon={<Cancel />}
                                        onClick={handleDisableDraftMode}
                                    >
                                        Abbrechen
                                    </Button>
                                </Stack>
                            </Stack>
                        </Collapse>
                    </Box>
                </Box>
                <ApprovalDialog
                    open={isDiscardApprovalDialogOpen}
                    title="Änderungen verwerfen?"
                    content="Dies kann nicht Rückgängig gemacht werden!"
                    onDecline={()=> setIsDiscardApprovalDialogOpen(false)}
                    onApprove={() => {
                        onDiscard?.();
                        setIsDiscardApprovalDialogOpen(false);
                    }}
                    approveButtonLabel="Ja, alles verwerfen!"
                />
            </>
        );
    }

    return null;
}
